import React, { FC } from 'react';

import { Icon } from '@rbilabs/components-library';
import { Helmet } from 'react-helmet-async';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';

import { Accordion, AccordionItem } from 'components/accordion';
import List, { ListItem, ListItemContent, ListItemSubtext, ListItemTitle } from 'components/list';
import SanityBlockRenderer from 'components/sanity-block-renderer';
import { useSupportData } from 'hooks/use-support-data/use-feature-navigation';
import AuthRequired from 'pages/authentication/auth-required';
import noop from 'utils/noop';
import { routes } from 'utils/routing';

import {
  PageHeading,
  PageWrap,
  SectionHeading,
  SpreadContentContainer,
  StyledBackButton,
  UnstyledLink,
} from './styled';
import { IReportOrderIssuePageProps, IReportOrderIssuePageViewProps } from './types';

export const ReportOrderIssuePage: FC<IReportOrderIssuePageProps> = () => {
  const { formatMessage } = useIntl();
  const { isSupportDataLoading, supportDataFaqs } = useSupportData();
  const { orderId } = useParams();

  if (isSupportDataLoading) {
    return null;
  }

  const faqs =
    supportDataFaqs?.map(faq => {
      return {
        title: faq?.supportFaqTitle?.locale ?? '',
        content: faq?.supportFaqContent?.localeRaw,
      };
    }) || [];

  const links: IReportOrderIssuePageViewProps['links'] = [
    {
      text: formatMessage({ id: 'orderNeverArrived' }),
      subtext: formatMessage({ id: 'confirmAllItemsAreMissing' }),
      to: `${routes.missingOrder}/${orderId}`,
    },
    {
      text: formatMessage({ id: 'issueWithMyItems' }),
      subtext: formatMessage({ id: 'confirmOnlySomeItemsAreMissing' }),
      to: `${routes.missingItems}/${orderId}`,
    },
  ];

  return (
    <AuthRequired>
      <Helmet title={formatMessage({ id: 'reportOrderIssuePageTitle' })} />
      <ReportOrderIssuePageView
        headingText={formatMessage({ id: 'reportOrderIssuePageTitle' })}
        linksHeadingText={formatMessage({ id: 'reportAnIssue' })}
        faqHeadingText={formatMessage({ id: 'faq' })}
        faqs={faqs}
        links={links}
        onBack={() => window.history.back()}
      />
    </AuthRequired>
  );
};

export const ReportOrderIssuePageView: FC<IReportOrderIssuePageViewProps> = ({
  headingText = '',
  linksHeadingText = '',
  faqHeadingText = [],
  links = [],
  faqs = [],
  onBack = noop,
  onFaqInteraction = noop,
}) => {
  return (
    <PageWrap>
      <PageHeading as="h1">{headingText}</PageHeading>
      <StyledBackButton onClick={onBack} />
      <ListItemContent>
        <SectionHeading>{linksHeadingText}</SectionHeading>
      </ListItemContent>
      <List>
        {links.map((link, i) => (
          <ListItem key={i}>
            <UnstyledLink to={link.to}>
              <ListItemContent>
                <SpreadContentContainer>
                  <div>
                    <ListItemTitle>{link.text}</ListItemTitle>
                    <ListItemSubtext>{link.subtext}</ListItemSubtext>
                  </div>
                  <Icon icon="next" color="icon-default" width="16px" aria-hidden />
                </SpreadContentContainer>
              </ListItemContent>
            </UnstyledLink>
          </ListItem>
        ))}
      </List>
      <ListItemContent>
        <SectionHeading>{faqHeadingText}</SectionHeading>
      </ListItemContent>
      {faqs.length > 0 && (
        <Accordion>
          {faqs.map((faq, i) => (
            <AccordionItem
              title={faq.title}
              key={i}
              onInteract={isExpanded => onFaqInteraction(isExpanded, faq)}
            >
              <SanityBlockRenderer content={faq.content} />
            </AccordionItem>
          ))}
        </Accordion>
      )}
    </PageWrap>
  );
};
